/* eslint-disable @typescript-eslint/no-explicit-any */

import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import { ArrowsPointingOutIcon, ArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { JSXElementConstructor, MouseEvent, ReactElement, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface RenderNodeProps {
    render: ReactElement<any, string | JSXElementConstructor<any>>;
}

const RenderNode = ({ render }: RenderNodeProps) => {
    const { id } = useNode();
    const { actions, query, isActive } = useEditor((_, query) => ({
        isActive: query.getEvent("selected").contains(id),
    }));
    const {
        isHover,
        dom,
        name,
        moveable,
        deletable,
        connectors: { drag },
        parent,
    } = useNode((node) => ({
        isHover: node.events.hovered,
        dom: node.dom,
        name: node.data.custom.displayName || node.data.displayName,
        moveable: query.node(node.id).isDraggable(),
        deletable: query.node(node.id).isDeletable() && node.data.custom?.canDelete !== false,
        parent: node.data.parent || undefined,
        props: node.data.props,
    }));

    const currentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (dom) {
            dom.classList.toggle("component-selected", isActive || isHover);
        }
    }, [dom, isActive, isHover]);

    const getPosition = useCallback((element: HTMLElement | null) => {
        const { top, left, bottom } = element ? element.getBoundingClientRect() : { top: 0, left: 0, bottom: 0 };
        return {
            top: `${top > 0 ? top : bottom}px`,
            left: `${left}px`,
        };
    }, []);

    const onScroll = useCallback(() => {
        const { current: currentDom } = currentRef;

        if (!currentDom || !dom) return;

        const { top, left } = getPosition(dom);
        currentDom.style.top = top;
        currentDom.style.left = left;
    }, [dom, getPosition]);

    useEffect(() => {
        document.getElementById("content")?.addEventListener("scroll", onScroll);
        document.querySelector(".craftjs-renderer")?.addEventListener("scroll", onScroll);

        return () => {
            document.getElementById("content")?.removeEventListener("scroll", onScroll);
            document.querySelector(".craftjs-renderer")?.removeEventListener("scroll", onScroll);
        };
    }, [onScroll]);

    const onSelectParent = () => {
        actions.selectNode(parent);
    };

    const onDelete = (e: MouseEvent) => {
        e.stopPropagation();
        actions.delete(id);
    };

    return (
        <>
            {(isHover || isActive) &&
                createPortal(
                    <div
                        ref={currentRef}
                        className="fixed flex items-center h-[30px] -mt-[29px] text-xs/tight text-white bg-primary-500 p-2 z-50"
                        style={{
                            left: getPosition(dom).left,
                            top: getPosition(dom).top,
                        }}
                    >
                        <span className="flex-1">{name}</span>

                        {(moveable || id !== ROOT_NODE || deletable) && <span className="w-2" />}

                        {moveable && (
                            <a className="flex items-center ml-2" ref={drag as any}>
                                <ArrowsPointingOutIcon className="h-4 w-4 rotate-45 text-white" />
                            </a>
                        )}

                        {id !== ROOT_NODE && (
                            <a className="flex items-center ml-2" onClick={onSelectParent}>
                                <ArrowUpIcon className="h-4 w-4 text-white" />
                            </a>
                        )}

                        {deletable && (
                            <a className="flex items-center ml-2" onMouseDown={onDelete}>
                                <TrashIcon className="h-4 w-4 text-white" />
                            </a>
                        )}
                    </div>,
                    document.querySelector("#page-container")!
                )}
            {render}
        </>
    );
};

export default RenderNode;
